// src/App.tsx
import React, { useState, useEffect } from 'react';
import RestaurantInfo from './components/RestaurantInfo';
import EnhancedMenuContainer from './components/EnhancedMenuContainer'; // Changed to new component
import Loading from './components/Loading';
import ErrorMessage from './components/ErrorMessage';
import LanguageSelector from './components/LanguageSelector';
import { AlertTriangle } from 'lucide-react';
import { ErrorState, MenuData, _StoreInfo as StoreInfo } from './types';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import FeedbackButton from './components/FeedBackButton';

const API_KEY = 'YWxpLTEyMzQ1MTEyNDU2NTQzMg';
const STORE_INFO_API = 'https://gandalf.orwi.app/api/store/storeInfo';
const STORE_MENU_API = 'https://gandalf.orwi.app/api/store/storeMenu';

// Wrapper App that provides language context
function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

function AppContent() {
  const { t, region } = useLanguage();
  
  // Get storeId from URL parameter
  const getStoreIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('storeId');
  };
  
  const storeId = getStoreIdFromUrl();
  
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [menu, setMenu] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const fetchData = async (): Promise<void> => {
    if (!storeId) {
      setError({
        title: t('qrError.title'),
        message: t('qrError.message'),
        type: 'qr_error'
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setReconnecting(false);
      
      // Fetch store info with language header
      const storeInfoResponse = await fetch(STORE_INFO_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Apikey': API_KEY,
          'Region': region
        },
        body: JSON.stringify({ id: storeId })
      });
      
      if (!storeInfoResponse.ok) {
        throw new Error(`${t('error.title')}. ${t('error.status')}: ${storeInfoResponse.status}`);
      }
      
      const storeInfoData = await storeInfoResponse.json();
      if (!storeInfoData.response) {
        throw new Error(t('error.invalidResponse'));
      }
      
      setStoreInfo(storeInfoData.response);
      
      // Fetch menu with language header
      const menuResponse = await fetch(STORE_MENU_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Apikey': API_KEY,
          'Region': region
        },
        body: JSON.stringify({ id: storeId })
      });
      
      if (!menuResponse.ok) {
        throw new Error(`${t('error.menuFetchFailed')}. ${t('error.status')}: ${menuResponse.status}`);
      }
      
      const menuData = await menuResponse.json();
      if (!menuData.response) {
        throw new Error(t('error.invalidMenuResponse'));
      }
      
      setMenu(menuData.response);
      
    } catch (err: unknown) {
      console.error(t('error.fetchError'), err);
      setError({
        title: t('error.connectionError'),
        message: err instanceof Error 
          ? `${err.message || t('error.defaultMessage')}`
          : t('error.defaultMessage'),
        type: 'connection_error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [storeId, region]); // Refetch when language/region changes

  // Function to handle retry
  const handleRetry = (): void => {
    setReconnecting(true);
    setError(null);
    
    // Add a small delay to show reconnecting state
    setTimeout(() => {
      fetchData();
    }, 1000);
  };

  if (loading) {
    return <Loading />;
  }
  
  if (reconnecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 animate-spin mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">{t('reconnect.title')}</h2>
        <p className="text-gray-500 mt-2">{t('reconnect.message')}</p>
      </div>
    );
  }

  if (error) {
    // QR code error view
    if (error.type === 'qr_error') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
          <div className="fixed top-4 right-4 z-10">
            <LanguageSelector />
          </div>
          <div className="p-6 bg-white rounded-lg shadow-md max-w-md w-full">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="mb-4 text-xl font-semibold text-center text-gray-900">{error.title}</h2>
            <p className="mb-5 text-center text-gray-600">{error.message}</p>
            <div className="bg-yellow-50 p-4 rounded-md mb-5">
              <p className="text-sm text-yellow-700">
                <strong>{t('qrError.tip').split(':')[0]}:</strong> {t('qrError.tip').split(':')[1]}
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition duration-150 ease-in-out"
            >
              {t('error.retry')}
            </button>
          </div>
        </div>
      );
    }
    
    // Regular error view
    return (
      <>
        <div className="fixed top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <ErrorMessage message={error.message} onRetry={handleRetry} />
      </>
    );
  }

  if (!storeInfo || !menu) {
    return (
      <>
        <div className="fixed top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <ErrorMessage message={t('error.loadFailed')} onRetry={handleRetry} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="fixed bottom-6 left-4 z-30">
        <LanguageSelector />
      </div>
      <RestaurantInfo storeInfo={storeInfo} />
      
      {/* Changed to EnhancedMenuContainer */}
      <EnhancedMenuContainer menu={menu} />
      
      {/* Feedback Button */}
      {storeId && <FeedbackButton storeId={storeId} />}
      
      {/* Footer */}
      <footer className="mt-10 py-6 bg-white border-t">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {storeInfo.name}
            </h3>
            <p className="text-gray-400 text-xs mt-1">
              {t('footer.digitalMenu')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;